import React, { useState, useEffect } from "react";
import {
  Typography,
  useTheme,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Chip,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  FormGroup,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { CaretDownIcon } from "~components/Icons";
import { authFetch } from "~helpers/authFetch";
import { useAuth } from "~contexts/Auth";
import { useToastNotificationContext } from "~contexts/ToastNotification";
import { CustomModal, CreateBrandModal } from "~components/atoms";

interface User {
  id: number;
  login: string;
  id_stream: number | null;
  role: string;
  brands: Array<{ id: number; name: string }>;
  permissions: Array<{
    id: number;
    id_stream: number;
    screen: string;
    stream?: {
      id_stream: number;
      stream_name: string;
      brand: string;
      sales_channel: number;
    };
  }>;
}

interface Brand {
  id_stream: number;
  brand: string;
  stream_name: string;
  sales_channel: number;
  brand_id: number;
}

interface Screen {
  value: string;
  label: string;
}

interface Permission {
  id_stream: number;
  screen: string;
}

const Users: React.FC = () => {
  const theme: any = useTheme();
  const { user: currentUser } = useAuth();
  const { setToastData } = useToastNotificationContext();
  const [users, setUsers] = useState<User[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [screens, setScreens] = useState<Screen[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAddBrandModalOpen, setIsAddBrandModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [expandedBrandId, setExpandedBrandId] = useState<number | null>(null); // id_stream do brand expandido

  // Form state
  const [formData, setFormData] = useState({
    login: "",
    password: "",
    role: "usuario",
    permissions: [] as Permission[],
  });


  useEffect(() => {
    loadUsers();
    loadBrands();
    loadScreens();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await authFetch({
        path: `${process.env.REACT_APP_BACKEND_URL}/users`,
        method: "GET",
      });
      const data = await response.json();
      setUsers(data);
    } catch (error) {
      console.error("Error loading users:", error);
      setToastData(
        {
          title: "Erro",
          text: "Erro ao carregar usuários",
        },
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const loadBrands = async () => {
    try {
      const response = await authFetch({
        path: `${process.env.REACT_APP_BACKEND_URL}/users/streams/list`,
        method: "GET",
      });
      const data = await response.json();
      // Usar todas as streams (não precisa filtrar por brand_id)
      setBrands(data);
    } catch (error) {
      console.error("Error loading streams:", error);
    }
  };

  const loadScreens = async () => {
    try {
      const response = await authFetch({
        path: `${process.env.REACT_APP_BACKEND_URL}/users/screens/list`,
        method: "GET",
      });
      const data = await response.json();
      setScreens(data);
    } catch (error) {
      console.error("Error loading screens:", error);
    }
  };

  const handleOpenCreateModal = async () => {
    setEditingUser(null);
    setFormData({
      login: "",
      password: "",
      role: "usuario",
      permissions: [],
    });
    setExpandedBrandId(null);
    // Recarregar brands do banco antes de abrir o modal
    await loadBrands();
    setIsModalOpen(true);
  };

  const handleOpenEditModal = async (user: User) => {
    console.log("=== Opening edit modal ===", {
      userId: user.id,
      userPermissions: user.permissions,
      permissionsCount: user.permissions?.length || 0,
    });
    // Recarregar streams do banco antes de abrir o modal
    await loadBrands();
    setEditingUser(user);
    // Filtrar permissões para garantir que apenas streams existentes sejam incluídas
    // E excluir "home" que nunca é salva (sempre permitida se tiver qualquer permissão da stream)
    const streamIds = brands.map((b) => b.id_stream);
    const mappedPermissions = user.permissions
      .filter((p) => streamIds.includes(p.id_stream) && p.screen !== "home")
      .map((p) => ({
        id_stream: p.id_stream,
        screen: p.screen,
      }));
    console.log("=== Mapped permissions ===", { mappedPermissions });
    setFormData({
      login: user.login,
      password: "",
      role: user.role,
      permissions: mappedPermissions,
    });
    setExpandedBrandId(null);
    setIsModalOpen(true);
  };

  const handleOpenDeleteModal = (user: User) => {
    setUserToDelete(user);
    setIsDeleteModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setExpandedBrandId(null);
  };

  const handleCloseAddBrandModal = () => {
    setIsAddBrandModalOpen(false);
  };

  const handleBrandCreated = async () => {
    // Recarregar lista de marcas após criar marca
    await loadBrands();
  };

  const handleSaveUser = async () => {
    if (isSaving) return; // Prevenir múltiplos cliques
    
    try {
      setIsSaving(true);
      
      // Garantir que as brands estejam carregadas
      if (brands.length === 0) {
        await loadBrands();
      }
      
      // Filtrar "home" das permissões - home é sempre permitida se tiver qualquer permissão da brand
      const permissionsWithoutHome = formData.permissions.filter(
        (perm) => perm.screen !== "home"
      );

      const payload = {
        login: formData.login,
        role: formData.role,
        permissions: permissionsWithoutHome,
        // Incluir senha apenas se não estiver vazia (para edição) ou sempre (para criação)
        ...(formData.password && formData.password.trim() !== '' && { password: formData.password.trim() }),
      };

      console.log("=== Saving user ===", {
        editingUser: editingUser?.id,
        payloadPermissions: payload.permissions,
        formDataPermissions: formData.permissions,
      });

      if (editingUser) {
        const response = await authFetch({
          path: `${process.env.REACT_APP_BACKEND_URL}/users/${editingUser.id}`,
          method: "PUT",
          data: payload,
        });
        const updatedUser = await response.json();
        console.log("=== User updated ===", {
          userId: updatedUser.id,
          savedPermissions: updatedUser.permissions,
        });
        setToastData(
          {
            title: "Sucesso",
            text: "Usuário atualizado com sucesso",
          },
          "success"
        );
      } else {
        if (!formData.password) {
          setToastData(
            {
              title: "Erro",
              text: "Senha é obrigatória para novos usuários",
            },
            "error"
          );
          return;
        }
        await authFetch({
          path: `${process.env.REACT_APP_BACKEND_URL}/users`,
          method: "POST",
          data: payload,
        });
        setToastData(
          {
            title: "Sucesso",
            text: "Usuário criado com sucesso",
          },
          "success"
        );
      }

      handleCloseModal();
      setEditingUser(null);
      setFormData({
        login: "",
        password: "",
        role: "usuario",
        permissions: [],
      });
      await loadUsers();
    } catch (error: any) {
      console.error("Error saving user:", error);
      const errorMessage =
        error.status === 400
          ? "Login já existe ou dados inválidos"
          : "Erro ao salvar usuário";
      setToastData(
        {
          title: "Erro",
          text: errorMessage,
        },
        "error"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      await authFetch({
        path: `${process.env.REACT_APP_BACKEND_URL}/users/${userToDelete.id}`,
        method: "DELETE",
      });
      setToastData(
        {
          title: "Sucesso",
          text: "Usuário deletado com sucesso",
        },
        "success"
      );
      setIsDeleteModalOpen(false);
      setUserToDelete(null);
      loadUsers();
    } catch (error: any) {
      console.error("Error deleting user:", error);
      const errorMessage =
        error.status === 400
          ? "Você não pode excluir sua própria conta"
          : "Erro ao deletar usuário";
      setToastData(
        {
          title: "Erro",
          text: errorMessage,
        },
        "error"
      );
    }
  };

  const togglePermission = (streamId: number, screen: string) => {
    setFormData((prev) => {
      const existing = prev.permissions.find(
        (p) => p.id_stream === streamId && p.screen === screen
      );
      const newPermissions = existing
        ? prev.permissions.filter((p) => !(p.id_stream === streamId && p.screen === screen))
        : [...prev.permissions, { id_stream: streamId, screen }];

      console.log("=== Toggling permission ===", {
        streamId,
        screen,
        wasChecked: !!existing,
        previousCount: prev.permissions.length,
        newPermissionsCount: newPermissions.length,
        newPermissions,
      });

      return {
        ...prev,
        permissions: newPermissions,
      };
    });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Função para agrupar permissões por stream
  const getPermissionsByStream = (userPermissions: User["permissions"]) => {
    const grouped: Array<{ id_stream: number; brand: string; stream_name: string; count: number }> = [];
    
    // Obter id_streams únicos das permissões (excluindo "home" que nunca é salva)
    const streamIds = [...new Set(
      userPermissions
        .filter((p) => p.screen !== "home")
        .map((p) => p.id_stream)
    )];
    
    streamIds.forEach((streamId) => {
      // Buscar stream no banco
      const stream = brands.find((b) => b.id_stream === streamId);
      
      if (stream) {
        // Contar apenas permissões para esta stream (excluindo "home" que é automática)
        const permissionsCount = userPermissions.filter(
          (p) => p.id_stream === streamId && p.screen !== "home"
        ).length;
        
        // Só adicionar se tiver pelo menos uma permissão (não contar home)
        if (permissionsCount > 0) {
          grouped.push({
            id_stream: stream.id_stream,
            brand: stream.brand.toUpperCase(),
            stream_name: stream.stream_name,
            count: permissionsCount,
          });
        }
      }
    });
    
    return grouped;
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ color: theme.palette.grey[0], mb: 0.5, fontSize: "30px !important" }}>
            gerenciamento de usuários
          </Typography>
          <Typography variant="body2" sx={{ color: "#858585", mb: "40px", fontSize: "15px" }}>
            administre acessos e informações dos usuários
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={handleOpenCreateModal}
          sx={{
            width: "155px",
            height: "48px",
            borderRadius: "100px",
            paddingTop: "16px",
            paddingRight: "24px",
            paddingBottom: "16px",
            paddingLeft: "24px",
            gap: "12px",
            fontWeight: 400,
            fontSize: "11px",
            textTransform: "none",
          }}
        >
          novo usuário +
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ width: "100%", overflowX: "hidden" }}>
        <Table sx={{ tableLayout: "fixed", width: "100%" }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  color: theme.palette.grey[0],
                  fontWeight: "bold",
                  padding: "24px",
                  width: "200px",
                  minWidth: "200px",
                  maxWidth: "200px",
                }}
              >
                Login
              </TableCell>
              <TableCell
                sx={{
                  color: theme.palette.grey[0],
                  fontWeight: "bold",
                  padding: "24px",
                  width: "150px",
                  minWidth: "150px",
                  maxWidth: "150px",
                }}
              >
                Perfil
              </TableCell>
              <TableCell
                sx={{ color: theme.palette.grey[0], fontWeight: "bold", padding: "24px" }}
              >
                Permissões
              </TableCell>
              <TableCell
                sx={{
                  color: theme.palette.grey[0],
                  fontWeight: "bold",
                  padding: "24px",
                  width: "24px",
                  minWidth: "24px",
                  maxWidth: "24px",
                }}
              ></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell
                  sx={{
                    color: theme.palette.grey[0],
                    padding: "24px",
                    width: "200px",
                    minWidth: "200px",
                    maxWidth: "200px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {user.login}
                </TableCell>
                <TableCell
                  sx={{
                    color: theme.palette.grey[0],
                    padding: "24px",
                    width: "150px",
                    minWidth: "150px",
                    maxWidth: "150px",
                  }}
                >
                  <Chip
                    label={user.role === "admin" ? "admin" : "usuário"}
                    size="small"
                    sx={{
                      width: "80px",
                      height: "26px",
                      backgroundColor: "#5C5C5C",
                      color: theme.palette.grey[0],
                      border: "none",
                      borderRadius: "1000px",
                      fontSize: "13px",
                      fontWeight: 400,
                      textTransform: "lowercase",
                    }}
                  />
                </TableCell>
                <TableCell sx={{ color: theme.palette.grey[0], padding: "24px" }}>
                  {user.permissions.length > 0 ? (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(4, 1fr)",
                        gap: "16px",
                      }}
                    >
                      {getPermissionsByStream(user.permissions).map(
                        (streamPerm, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "15px",
                                textTransform: "uppercase",
                                color: "#FFF",
                                fontWeight: 400,
                                lineHeight: 1.2,
                              }}
                            >
                              {streamPerm.brand}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "12px",
                                color: "#D6D6D6",
                                fontWeight: 400,
                                lineHeight: 1.2,
                                mb: 0.5,
                              }}
                            >
                              {streamPerm.stream_name}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "11px",
                                color: "#D6D6D6",
                                fontWeight: 400,
                                lineHeight: 1.2,
                              }}
                            >
                              {streamPerm.count} permissões
                            </Typography>
                          </Box>
                        )
                      )}
                    </Box>
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell sx={{ padding: "24px", width: "24px" }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      width: "fit-content",
                    }}
                  >
                    <Stack direction="column" spacing={1}>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEditModal(user)}
                        sx={{
                          color: theme.palette.grey[400],
                          padding: "4px",
                          width: "fit-content",
                          "&:hover": {
                            color: theme.palette.grey[0],
                          },
                        }}
                      >
                        <Box
                          component="img"
                          src="/icons/edit.png"
                          alt="editar"
                          sx={{
                            width: "24px",
                            height: "24px",
                          }}
                        />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDeleteModal(user)}
                        disabled={currentUser?.id_user_somalive === user.id}
                        sx={{
                          color: theme.palette.grey[400],
                          padding: "4px",
                          width: "fit-content",
                          "&:hover": {
                            color:
                              currentUser?.id_user_somalive === user.id
                                ? theme.palette.grey[400]
                                : theme.palette.error.main,
                          },
                          "&.Mui-disabled": {
                            opacity: 0.5,
                          },
                        }}
                      >
                        <Box
                          component="img"
                          src="/icons/delete.png"
                          alt="excluir"
                          sx={{
                            width: "24px",
                            height: "24px",
                          }}
                        />
                      </IconButton>
                    </Stack>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modal de Criar/Editar Usuário */}
      <CustomModal isOpened={isModalOpen} onClose={handleCloseModal}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            maxHeight: "90vh",
            overflow: "hidden",
          }}
        >
          <Typography
            variant="h6"
            sx={{ mb: 2, color: theme.palette.grey[0], flexShrink: 0 }}
          >
            {editingUser ? "Editar Usuário" : "Novo Usuário"}
          </Typography>

          <Box
            component="form"
            autoComplete="off"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              overflowY: "auto",
              overflowX: "hidden",
              pr: 1,
              flex: 1,
              minHeight: 0,
              pt: "20px",
            }}
          >
            <TextField
              label="Login"
              value={formData.login}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, login: e.target.value }))
              }
              fullWidth
              required
              disabled={!!editingUser}
              autoComplete="off"
            />

            <TextField
              label={editingUser ? "Nova Senha (deixe vazio para manter)" : "Senha"}
              type="password"
              value={formData.password}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, password: e.target.value }))
              }
              fullWidth
              required={!editingUser}
              autoComplete="new-password"
              inputProps={{
                autoComplete: "new-password",
              }}
            />

            <FormControl fullWidth>
              <InputLabel>Perfil</InputLabel>
              <Select
                value={formData.role}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, role: e.target.value }))
                }
                label="Perfil"
              >
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="chat">Chat</MenuItem>
                <MenuItem value="usuario">Usuário</MenuItem>
              </Select>
            </FormControl>

            {formData.role !== "admin" && (
              <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Typography variant="subtitle2" sx={{ color: theme.palette.grey[0] }}>
                    Permissões por Stream + Tela
                  </Typography>
                  {currentUser?.role === "admin" && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => setIsAddBrandModalOpen(true)}
                      sx={{
                        color: theme.palette.grey[0],
                        textTransform: "none",
                        fontSize: "14px",
                        "&:hover": {
                          backgroundColor: "transparent",
                        },
                      }}
                    >
                      adicionar marca +
                    </Button>
                  )}
                </Box>
                <Button
                  size="small"
                  variant="secondaryButton"
                  onClick={() => {
                    const filteredScreens = screens.filter((screen) => screen.value !== "home");
                    const allPermissions: Permission[] = [];
                    brands.forEach((brand) => {
                      filteredScreens.forEach((screen) => {
                        allPermissions.push({
                          id_stream: brand.id_stream,
                          screen: screen.value,
                        });
                      });
                    });

                    const allSelected =
                      allPermissions.length > 0 &&
                      allPermissions.every((perm) =>
                        formData.permissions.some(
                          (p) => p.id_stream === perm.id_stream && p.screen === perm.screen
                        )
                      );

                    setFormData((prev) => ({
                      ...prev,
                      permissions: allSelected ? [] : allPermissions,
                    }));
                  }}
                >
                  {brands.length > 0 &&
                  screens.filter((screen) => screen.value !== "home").length > 0 &&
                  brands.every((brand) =>
                    screens.filter((screen) => screen.value !== "home").every((screen) =>
                      formData.permissions.some(
                        (p) => p.id_stream === brand.id_stream && p.screen === screen.value
                      )
                    )
                  )
                    ? "Desmarcar Tudo"
                    : "Selecionar Tudo"}
                </Button>
              </Box>
              {brands.length === 0 ? (
                <Box sx={{ p: 2, textAlign: "center", color: theme.palette.grey[400] }}>
                  Carregando marcas...
                </Box>
              ) : (
                brands.map((brand) => {
                  const filteredScreens = screens.filter((screen) => screen.value !== "home");
                  // Contar apenas permissões que não são "home" (home é automática e oculta)
                  const streamPermissions = formData.permissions.filter(
                    (p) => p.id_stream === brand.id_stream && p.screen !== "home"
                  );
                  const permissionsCount = streamPermissions.length;
                  const allStreamScreensSelected =
                    filteredScreens.length > 0 &&
                    filteredScreens.every((screen) =>
                      streamPermissions.some((p) => p.screen === screen.value)
                    );

                return (
                  <Accordion
                    key={brand.id_stream}
                    expanded={expandedBrandId === brand.id_stream}
                    onChange={(_, isExpanded) => {
                      setExpandedBrandId(isExpanded ? brand.id_stream : null);
                    }}
                    sx={{
                      mb: 2,
                      backgroundColor: theme.palette.grey[900],
                      border: `1px solid ${theme.palette.grey[700]}`,
                      borderRadius: 1,
                      "&:before": {
                        display: "none",
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<CaretDownIcon />}
                      sx={{
                        px: 2,
                        py: 1.5,
                        "& .MuiAccordionSummary-content": {
                          display: "flex",
                          alignItems: "center",
                          my: 0,
                          gap: "12px",
                        },
                        "& .MuiAccordionSummary-expandIconWrapper": {
                          color: theme.palette.grey[0],
                        },
                      }}
                    >
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ color: theme.palette.grey[0], fontWeight: 600 }}
                        >
                          {brand.brand.toUpperCase()}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: theme.palette.grey[400], fontSize: "12px" }}
                        >
                          {brand.stream_name}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          backgroundColor: "#5C5C5C",
                          borderRadius: "100px",
                          fontSize: "13px",
                          color: "#FFFFFF",
                          padding: "0 10px",
                          display: "inline-flex",
                          alignItems: "center",
                        }}
                      >
                        {permissionsCount} permissão{permissionsCount !== 1 ? "ões" : ""}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 2, pb: 2 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-start",
                          alignItems: "center",
                          mb: 2,
                        }}
                      >
                        <Button
                          size="small"
                          variant="secondaryButton"
                          onClick={() => {
                            if (allStreamScreensSelected) {
                              // Desmarcar todas as telas desta stream
                              setFormData((prev) => ({
                                ...prev,
                                permissions: prev.permissions.filter(
                                  (p) => p.id_stream !== brand.id_stream
                                ),
                              }));
                            } else {
                              // Selecionar todas as telas desta stream (exceto home)
                              const streamScreens = filteredScreens.map((screen) => ({
                                id_stream: brand.id_stream,
                                screen: screen.value,
                              }));
                              // Remover permissões existentes desta stream e adicionar todas
                              setFormData((prev) => ({
                                ...prev,
                                permissions: [
                                  ...prev.permissions.filter(
                                    (p) => p.id_stream !== brand.id_stream
                                  ),
                                  ...streamScreens,
                                ],
                              }));
                            }
                          }}
                          sx={{ fontSize: "0.75rem", py: 0.5 }}
                        >
                          {allStreamScreensSelected ? "Desmarcar Tudo" : "Selecionar Tudo"}
                        </Button>
                      </Box>
                      <FormGroup
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          flexWrap: "wrap",
                          gap: "4px",
                        }}
                      >
                        {filteredScreens.map((screen) => {
                          const checked = formData.permissions.some(
                            (p) => p.id_stream === brand.id_stream && p.screen === screen.value
                          );

                          return (
                            <FormControlLabel
                              key={screen.value}
                              control={
                                <Switch
                                  checked={checked}
                                  onChange={() => togglePermission(brand.id_stream, screen.value)}
                                  sx={{ mr: 1 }}
                                />
                              }
                              label={screen.label}
                              sx={{
                                width: "calc(50% - 2px)",
                                minWidth: 0,
                                margin: 0,
                              }}
                            />
                          );
                        })}
                      </FormGroup>
                    </AccordionDetails>
                  </Accordion>
                );
              }))}
            </Box>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              mt: 3,
              justifyContent: "flex-start",
              flexShrink: 0,
              borderTop: `1px solid ${theme.palette.grey[700]}`,
              pt: 2,
            }}
          >
            <Button
              variant="contained"
              onClick={handleSaveUser}
              disabled={isSaving}
              sx={{
                height: "48px",
                minWidth: "150px",
              }}
            >
              {isSaving ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={20} sx={{ color: "#FFF" }} />
                  Salvando...
                </Box>
              ) : (
                "salvar alterações"
              )}
            </Button>
            <Button
              variant="text"
              onClick={handleCloseModal}
              disabled={isSaving}
              sx={{
                color: "#FFFFFF",
                backgroundColor: "transparent",
                "&:hover": {
                  backgroundColor: "transparent",
                },
                "&:disabled": {
                  color: "#858585",
                },
              }}
            >
              Cancelar
            </Button>
          </Box>
        </Box>
      </CustomModal>

      {/* Modal de Confirmação de Exclusão */}
      <CustomModal
        isOpened={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
      >
        <Typography variant="h6" sx={{ fontSize: "23px", color: theme.palette.grey[0] }}>
          deseja deletar o usuário <strong>{userToDelete?.login}</strong>?
        </Typography>
        <Typography sx={{ mb: 2, fontSize: "11px", color: "#858585" }}>
          não será mais possível logar com esse perfil.
        </Typography>
        {currentUser?.id_user_somalive === userToDelete?.id && (
          <Alert severity="error" sx={{ mb: 2, color: "#858585" }}>
            Você não pode excluir sua própria conta!
          </Alert>
        )}
        <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
          <Button
            variant="cancelButton"
            color="error"
            sx={{ height: "48px" }}
            onClick={handleDeleteUser}
            disabled={currentUser?.id_user_somalive === userToDelete?.id}
          >
            deletar esse usuário
          </Button>
          <Button variant="secondaryButton" sx={{ height: "48px" }} onClick={() => setIsDeleteModalOpen(false)}>
            cancelar
          </Button>
        </Box>
      </CustomModal>

      {/* Modal de Adicionar Marca */}
      <CreateBrandModal
        isOpened={isAddBrandModalOpen}
        onClose={handleCloseAddBrandModal}
        onSuccess={handleBrandCreated}
      />
    </Box>
  );
};

export default Users;
